<script>
import options from "./CheckoutStepOne.options";
export default options;
</script>

<template>
  <article
    class="kb-forms kb-checkout-step-one"
    aria-live="polite"
    :class="{ 'kb-checkout-step-one--submitting': isSubmitting }"
  >
    <section class="kb-checkout-step-one__customer-info">
      <div class="kb-checkout-step-one__radio">
        <fieldset class="kb-checkout-step-one__radio-fieldset">
          <legend class="kb-checkout-step-one__radio-legend">{{ getLabel("customerInfoLegend") }}</legend>
          <label class="kb-checkout-step-one__radio-label">
            <input
              type="radio"
              name="__field_5898"
              value="private"
              v-model="customerTypeInput"
              class="FormChoice__Input FormChoice__Input--Radio"
              data-f-datainput
            />
            {{ getLabel("privateCustomerText") }}
          </label>
          <label class="kb-checkout-step-one__radio-label">
            <input
              type="radio"
              name="__field_5898"
              value="company"
              v-model="customerTypeInput"
              class="FormChoice__Input FormChoice__Input--Radio"
              data-f-datainput
            />
            {{ getLabel("companyCustomerText") }}
          </label>
          <label class="kb-checkout-step-one__radio-label">
            <input
              type="radio"
              name="__field_5898"
              value="publicInstitution"
              v-model="customerTypeInput"
              class="FormChoice__Input FormChoice__Input--Radio"
              data-f-datainput
            />
            {{ getLabel("publicInstitutionCustomerText") }}
          </label>
        </fieldset>
      </div>

      <template v-if="isCompany">
        <section class="kb-checkout-step-one__form-wrapper--insert">
          <div class="kb-checkout-step-one__form-wrapper">
            <FormInputTextField
              isRequired
              v-model="companyNameInput"
              fieldName="companyName"
              :label="getLabel('companyNameLabel')"
              :isValidationError="!companyNameInputFilled"
              :placeholder="getLabel('companyNamePlaceholder')"
              :errorMessageLabel="getLabel('validationCompanyName')"
            />

            <FormInputTextField
              v-model="companyDepartmentInput"
              fieldName="companyDepartment"
              :label="getLabel('companyDepartmentLabel')"
              :isValidationError="!companyDepartmentInputFilled"
              :placeholder="getLabel('companyDepartmentPlaceholder')"
              :errorMessageLabel="getLabel('validationCompanyDepartment')"
            />
          </div>

          <div
            class="kb-checkout-step-one__form-wrapper kb-checkout-step-one__form-wrapper--one-input-half-full"
          >
            <FormInputTextField
              isRequired
              v-model="companyCvrInput"
              fieldName="companyCvr"
              :label="getLabel('companyCvrLabel')"
              :isValidationError="!companyCvrInputFilled"
              :placeholder="getLabel('companyCvrPlaceholder')"
              :errorMessageLabel="getLabel('validationRequired')"
            />
          </div>
        </section>
      </template>

      <template v-if="isPublicInstitution">
        <div class="kb-checkout-step-one__form-wrapper--insert">
          <div class="kb-checkout-step-one__form-wrapper">
            <FormInputTextField
              isRequired
              v-model="publicInstitutionNameInput"
              fieldName="publicInstitutionName"
              :label="getLabel('publicInstitutionNameLabel')"
              :isValidationError="!publicInstitutionNameInputFilled && !hideAllErrors"
              :placeholder="getLabel('publicInstitutionNamePlaceholder')"
              :errorMessageLabel="getLabel('validationPublicInstitutionName')"
            />

            <FormInputTextField
              v-model="publicInstitutionUserId"
              fieldName="publicInstitutionUserId"
              :label="getLabel('publicInstitutionUserIdLabel')"
              :isValidationError="!publicInstitutionUserIdFilled && !hideAllErrors"
              :placeholder="getLabel('publicInstitutionUserIdPlaceholder')"
              :errorMessageLabel="getLabel('validationPublicInstitutionUserId')"
            />
          </div>

          <div class="kb-checkout-step-one__form-wrapper">
            <FormInputTextField
              v-model="publicInstitutionDepartmentOrContact"
              fieldName="publicInstitutionDepartmentOrContact"
              :label="getLabel('publicInstitutionDepartmentOrContactLabel')"
              :isValidationError="!publicInstitutionDepartmentOrContactFilled && !hideAllErrors"
              :placeholder="
                getLabel('publicInstitutionDepartmentOrContactPlaceholder')
              "
              :errorMessageLabel="
                getLabel('validationPublicInstitutionDepartmentOrContact')
              "
            />

            <FormInputTextField
              isRequired
              v-model="publicInstitutionEan"
              fieldName="publicInstitutionEan"
              :label="getLabel('publicInstitutionEanLabel')"
              :isValidationError="!publicInstitutionEanFilled && !hideAllErrors"
              :placeholder="getLabel('publicInstitutionEanPlaceholder')"
              :errorMessageLabel="getLabel('validationRequired')"
            />
          </div>
        </div>
      </template>

      <div class="kb-checkout-step-one__form-wrapper">
        <FormInputTextField
          isRequired
          v-model="firstNameInput"
          fieldName="firstName"
          autocomplete="given-name"
          :label="
            !isCompany
              ? getLabel('firstNameLabel')
              : getLabel('companyContactFirstNameLabel')
          "
          :placeholder="
            !isCompany
              ? getLabel('firstNamePlaceholder')
              : getLabel('companyContactFirstNamePlaceholder')
          "
          :isValidationError="!firstNameInputFilled && !hideAllErrors"
          :errorMessageLabel="
            !isCompany
              ? getLabel('validationFirstName')
              : getLabel('validationCompanyContactFirstName')
          "
        />

        <FormInputTextField
          isRequired
          v-model="lastNameInput"
          fieldName="lastName"
          autocomplete="family-name"
          :label="
            !isCompany
              ? getLabel('lastNameLabel')
              : getLabel('companyContactLastNameLabel')
          "
          :placeholder="
            !isCompany
              ? getLabel('lastNamePlaceholder')
              : getLabel('companyContactLastNamePlaceholder')
          "
          :isValidationError="!lastNameInputFilled && !hideAllErrors"
          :errorMessageLabel="
            !isCompany
              ? getLabel('validationLastName')
              : getLabel('validationCompanyContactLastName')
          "
        />
      </div>

      <div class="kb-checkout-step-one__form-wrapper">
        <FormInputTextField
          isRequired
          v-model="emailInput"
          fieldName="email"
          autocomplete="email"
          :label="getLabel('emailLabel')"
          :placeholder="getLabel('emailPlaceholder')"
          :isValidationError="!emailInputFilled && !hideAllErrors"
          :errorMessageLabel="getLabel('validationEmail')"
        />

        <FormInputTextField
          v-model="phoneInput"
          fieldName="phone"
          autcomplete="tel"
          isRequired
          :label="getLabel('phoneLabel')"
          :placeholder="getLabel('phonePlaceholder')"
          :isValidationError="!phoneInputFilled && !hideAllErrors"
          :errorMessageLabel="getLabel('validationPhone')"
        />
      </div>
      <div class="kb-checkout-step-one__form-wrapper">
        <FormAddressField
          v-model:address-line-value="addressLineInput"
          v-model:zip-code-value="postalCodeInput"
          v-model:city-value="cityInput"
          :addressLabel="getLabel('addressLineLabel')"
          :zipCodeLabel="getLabel('postalCodeLabel')"
          :addressPlaceholder="getLabel('addressLinePlaceholder')"
          :zipCodePlaceholder="getLabel('postalCodePlaceholder')"
          :cityPlaceholder="getLabel('cityPlaceholder')"
          :cityLabel="getLabel('cityLabel')"
          :isValidationError="!addressLineInputFilled && !hideAllErrors"
          :errorMessageLabel="getLabel('validationAddressLine')"
        />
      </div>
    </section>

    <div class="kb-checkout-step-one__navigation">
      <div class="kb-checkout-step-one__navigation-breaker">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 704 1" aria-hidden="true" role="presentation">
          <path fill="#DFDFDB" d="M0 0h704v1H0z" />
        </svg>
      </div>
      <div class="kb-checkout-step-one__navigation-button-wrapper">
        <a :href="getLabel('cancelButtonUrl')" class="kb-button">{{
          getLabel("cancelButtonText")
        }}</a>
        <button
          @click="onSubmit"
          class="kb-checkout-step-one__bottom-button submit kb-button kb-button--full-color-spot"
        >
          {{ getLabel("submitButtonTextStep1") }}
        </button>
      </div>
    </div>

    <template v-if="isSubmitting">
      <div class="kb-spinner-overlay">
        <div
          class="kb-spinner-overlay__spinner kb-spinner-overlay__spinner--sticky"
        ></div>
      </div>
    </template>
  </article>
</template>
